/**
 * Shared layout for every wizard screen: safe area, header with step
 * indicator (e.g. "3 of 6"), optional back button, content slot, and a
 * fixed-bottom primary button. Keeps each screen focused on its
 * specific question rather than repeating chrome.
 */
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ms, s } from '../../../utils/responsive';

type Props = {
  stepNumber: number;   // 1-based position in the wizard
  totalSteps: number;
  title: string;
  subtitle?: string;
  primaryLabel: string;
  onPrimaryPress: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  onBackPress?: () => void;
  secondaryLabel?: string;       // e.g. "Skip for now"
  onSecondaryPress?: () => void;
  children: React.ReactNode;
};

export const WizardShell: React.FC<Props> = ({
  stepNumber,
  totalSteps,
  title,
  subtitle,
  primaryLabel,
  onPrimaryPress,
  primaryDisabled,
  primaryLoading,
  onBackPress,
  secondaryLabel,
  onSecondaryPress,
  children,
}) => {
  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      {/* KeyboardAvoidingView keeps the Continue/Finish button visible
          above the keyboard on screens with text inputs (Profile,
          Successor soft-ask). On iOS 'padding' shrinks the whole
          layout; on Android the default adjustResize handles it, but
          we still add 'height' behavior so short screens don't jump. */}
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.header}>
          {onBackPress ? (
            <TouchableOpacity
              style={styles.backButton}
              onPress={onBackPress}
              accessibilityLabel="Go back"
            >
              <Image
                source={require('../../../assets/icons/back.png')}
                style={styles.backIcon}
              />
            </TouchableOpacity>
          ) : (
            <View style={styles.backButtonPlaceholder} />
          )}
          <Text style={styles.stepIndicator}>
            Step {stepNumber} of {totalSteps}
          </Text>
          <View style={styles.backButtonPlaceholder} />
        </View>

        <View style={styles.progressBarTrack}>
          <View
            style={[styles.progressBarFill, { width: `${(stepNumber / totalSteps) * 100}%` }]}
          />
        </View>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
        >
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          {children}
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.primaryButton, primaryDisabled && styles.primaryButtonDisabled]}
            onPress={onPrimaryPress}
            disabled={primaryDisabled || primaryLoading}
            accessibilityLabel={primaryLabel}
          >
            {primaryLoading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
            )}
          </TouchableOpacity>
          {secondaryLabel && onSecondaryPress ? (
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={onSecondaryPress}
              accessibilityLabel={secondaryLabel}
            >
              <Text style={styles.secondaryButtonText}>{secondaryLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  flex: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingTop: ms(10),
    paddingBottom: ms(10),
  },
  backButton: {
    padding: ms(6),
    width: ms(40),
  },
  backButtonPlaceholder: {
    width: ms(40),
  },
  backIcon: {
    width: ms(24, 0.3),
    height: ms(24, 0.3),
    tintColor: '#1C2C4F',
  },
  stepIndicator: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
  },
  progressBarTrack: {
    height: ms(4),
    backgroundColor: '#E1E5EA',
    marginHorizontal: ms(20),
    borderRadius: ms(2),
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#29BFC0',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: ms(20),
    paddingBottom: ms(20),
  },
  title: {
    fontSize: ms(26, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(10),
    marginBottom: ms(10),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(20),
  },
  footer: {
    paddingHorizontal: ms(20),
    paddingTop: ms(10),
    paddingBottom: ms(10),
    backgroundColor: '#F3F6F8',
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(28),
    paddingVertical: ms(16),
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonDisabled: {
    backgroundColor: '#A7D9D9',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  secondaryButton: {
    alignItems: 'center',
    paddingVertical: ms(14),
    marginTop: ms(4),
  },
  secondaryButtonText: {
    color: '#1C2C4F',
    fontSize: ms(15, 0.3),
    fontWeight: '500',
  },
});
